import { onUnmounted, ref } from 'vue'

/**
 * @typedef {Object} VideoPlayerBindings
 * @property {import('vue').Ref<HTMLVideoElement[]>} videoRefs
 * @property {import('vue').Ref<boolean>} isAutoPlay
 * @property {import('vue').Ref<number>} currentCarouselIndex
 * @property {import('vue').Ref<number>} activeVideoIndex
 * @property {(el: HTMLVideoElement|null, index: number) => void} setVideoRef
 * @property {(index: number) => void} handleCarouselChange
 * @property {() => void} handleVideoPlay
 * @property {() => void} handleVideoPause
 * @property {() => void} handleVideoSeeking
 * @property {(event: Event) => void} handleVideoSeeked
 * @property {() => void} handleVideoEnded
 * @property {(videoEl: HTMLVideoElement|null) => void} bindVideoSeekFix
 * @property {(videoEl: HTMLVideoElement|null) => void} unbindVideoSeekFix
 * @property {(videoElements?: Iterable<HTMLVideoElement>|ArrayLike<HTMLVideoElement>|null) => void} syncVideoBindings
 * @property {import('vue').Ref<Record<string, boolean>>} viewportVideoPlayingStates
 * @property {(key: string|number) => HTMLVideoElement|null} getViewportVideo
 * @property {(element: HTMLVideoElement|null, key: string|number) => void} setViewportVideoRef
 * @property {(key: string|number, options?: {silent?: boolean, reset?: boolean}) => Promise<boolean>} playViewportVideo
 * @property {(key: string|number) => void} resetViewportVideo
 * @property {(key: string|number) => void} handleViewportVideoPlay
 * @property {(key: string|number) => void} handleViewportVideoPause
 * @property {(key: string|number) => void} handleViewportVideoEnded
 * @property {(key: string|number) => void} handleViewportVideoError
 */

/**
 * @description 通用视频播放器管理 composable
 * @reason 统一 Hero 轮播视频状态控制与普通 video 的 iOS Safari seek 修复，避免项目内多套实现分叉
 * @edge-cases
 *   1. BaseBanner 拖拽切换动画约 0.32s，淡入约 0.5s，切换后延迟 600ms 再显示 controls，
 *      确保移动端浏览器以正确全宽尺寸初始化视频控件
 *   2. seeking 期间 pause 事件先于 seeking 触发，通过 isVideoSeeking 标志防止误重置 isAutoPlay
 *   3. iOS Safari seek bar 结束后存在"粘性 seek"问题，通过临时关闭再恢复 controls 重置原生控件状态
 * @param {Object} [options]
 * @param {number} [options.viewportThreshold=0.35] - 入屏自动播放的可见比例。
 * @param {Function} [options.onViewportError] - 入屏视频播放失败回调。
 * @returns {VideoPlayerBindings}
 */
export function useVideoPlayer(options = {}) {
  const videoRefs = ref([])
  const isAutoPlay = ref(true)
  const currentCarouselIndex = ref(0)
  const activeVideoIndex = ref(-1)
  const isVideoSeeking = ref(false)
  const viewportVideoPlayingStates = ref({})
  const viewportVideoRefs = new Map()
  const viewportVideoKeys = new WeakMap()
  const viewportAutoplayedKeys = new Set()
  const viewportPendingKeys = new Set()
  const viewportThreshold = Number(options.viewportThreshold) || 0.35
  let viewportIntersectionObserver = null

  const seekFixBindings = new Map()
  const heroSeekTimers = {
    debounceTimer: null,
    blockTimer: null,
  }

  let controlsRevealTimer = null

  /**
   * @description 清理单组 seek 修复计时器
   * @param {{ debounceTimer: ReturnType<typeof setTimeout>|null, blockTimer: ReturnType<typeof setTimeout>|null }} timers
   * @returns {void}
   */
  const clearSeekTimers = (timers) => {
    clearTimeout(timers.debounceTimer)
    clearTimeout(timers.blockTimer)
    timers.debounceTimer = null
    timers.blockTimer = null
  }

  /**
   * @description iOS Safari seek 完成后临时重置 controls
   * @param {HTMLVideoElement|null} videoEl
   * @param {{ debounceTimer: ReturnType<typeof setTimeout>|null, blockTimer: ReturnType<typeof setTimeout>|null }} timers
   * @returns {void}
   */
  const scheduleControlsReset = (videoEl, timers) => {
    if (!videoEl || !import.meta.client) return

    clearSeekTimers(timers)
    timers.debounceTimer = setTimeout(() => {
      if (!videoEl.parentNode) return
      videoEl.controls = false
      timers.blockTimer = setTimeout(() => {
        if (!videoEl.parentNode) return
        videoEl.controls = true
      }, 50)
    }, 50)
  }

  /**
   * @description 为单个 video 元素绑定 iOS seek 修复
   * @param {HTMLVideoElement|null} videoEl
   * @returns {void}
   */
  const bindVideoSeekFix = (videoEl) => {
    if (!videoEl || !import.meta.client || seekFixBindings.has(videoEl)) return

    const timers = {
      debounceTimer: null,
      blockTimer: null,
    }

    const onSeeking = () => {
      clearSeekTimers(timers)
    }

    const onSeeked = () => {
      scheduleControlsReset(videoEl, timers)
    }

    videoEl.addEventListener('seeking', onSeeking)
    videoEl.addEventListener('seeked', onSeeked)

    seekFixBindings.set(videoEl, {
      cleanup: () => {
        clearSeekTimers(timers)
        videoEl.removeEventListener('seeking', onSeeking)
        videoEl.removeEventListener('seeked', onSeeked)
      },
    })
  }

  /**
   * @description 解绑单个 video 元素的 iOS seek 修复
   * @param {HTMLVideoElement|null} videoEl
   * @returns {void}
   */
  const unbindVideoSeekFix = (videoEl) => {
    if (!videoEl || !seekFixBindings.has(videoEl)) return
    const binding = seekFixBindings.get(videoEl)
    binding.cleanup()
    seekFixBindings.delete(videoEl)
  }

  /**
   * @description 同步当前组件内需要绑定 seek 修复的 video 集合
   * @param {Iterable<HTMLVideoElement>|ArrayLike<HTMLVideoElement>|null} [videoElements]
   * @returns {void}
   */
  const syncVideoBindings = (videoElements = []) => {
    const currentVideos = Array.from(videoElements || []).filter(Boolean)
    const currentVideoSet = new Set(currentVideos)

    seekFixBindings.forEach((_, videoEl) => {
      if (!currentVideoSet.has(videoEl)) {
        unbindVideoSeekFix(videoEl)
      }
    })

    currentVideos.forEach((videoEl) => {
      bindVideoSeekFix(videoEl)
    })
  }

  /**
   * @description 设置 Hero 轮播视频元素引用
   * @param {HTMLVideoElement|null} el
   * @param {number} index
   * @returns {void}
   */
  const setVideoRef = (el, index) => {
    if (!Number.isInteger(index) || index < 0) return
    videoRefs.value[index] = el || null
  }

  /**
   * @description 轮播切换时暂停所有视频并延迟显示控件
   * @param {number} index - 切换到的索引
   * @returns {void}
   */
  const handleCarouselChange = (index) => {
    currentCarouselIndex.value = index
    activeVideoIndex.value = -1
    clearSeekTimers(heroSeekTimers)

    if (controlsRevealTimer) {
      clearTimeout(controlsRevealTimer)
      controlsRevealTimer = null
    }

    videoRefs.value.forEach((video) => {
      if (video && !video.paused) {
        video.pause()
      }
    })

    controlsRevealTimer = setTimeout(() => {
      activeVideoIndex.value = index
      controlsRevealTimer = null
    }, 600)
  }

  /**
   * @description 视频播放时停止轮播自动切换
   * @returns {void}
   */
  const handleVideoPlay = () => {
    isAutoPlay.value = false
  }

  /**
   * @description 视频开始 seeking 时暂停轮播自动切换
   * @returns {void}
   */
  const handleVideoSeeking = () => {
    clearSeekTimers(heroSeekTimers)
    if (!isVideoSeeking.value) {
      isVideoSeeking.value = true
      isAutoPlay.value = false
    }
  }

  /**
   * @description 视频 seeking 结束时恢复轮播状态并修复 iOS seek 粘滞
   * @param {Event} event
   * @returns {void}
   */
  const handleVideoSeeked = (event) => {
    const videoEl = /** @type {HTMLVideoElement|null} */ (event.target)

    isVideoSeeking.value = false
    if (videoEl?.paused) {
      isAutoPlay.value = true
    }

    scheduleControlsReset(videoEl, heroSeekTimers)
  }

  /**
   * @description 视频暂停时恢复轮播自动切换
   * @returns {void}
   */
  const handleVideoPause = () => {
    isAutoPlay.value = true
  }

  /**
   * @description 视频结束时恢复轮播自动切换
   * @returns {void}
   */
  const handleVideoEnded = () => {
    isAutoPlay.value = true
  }

  const normalizeViewportVideoKey = (key) => String(key ?? '').trim()

  const setViewportVideoPlayingState = (key, isPlaying) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return
    viewportVideoPlayingStates.value = {
      ...viewportVideoPlayingStates.value,
      [normalizedKey]: isPlaying,
    }
  }

  const getViewportVideo = (key) => viewportVideoRefs.get(normalizeViewportVideoKey(key)) || null

  /**
   * @description 播放指定入屏视频并同步状态。
   * @param {string|number} key - 视频唯一键。
   * @param {{ silent?: boolean, reset?: boolean }} [playOptions]
   * @returns {Promise<boolean>} 是否成功开始播放。
   */
  const playViewportVideo = async (key, { silent = false, reset = true } = {}) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    const video = viewportVideoRefs.get(normalizedKey)
    if (!normalizedKey || !video) return false

    try {
      video.muted = true
      if (reset) {
        video.currentTime = 0
      }
      await video.play()
      viewportAutoplayedKeys.add(normalizedKey)
      setViewportVideoPlayingState(normalizedKey, true)
      return true
    } catch (error) {
      viewportAutoplayedKeys.delete(normalizedKey)
      setViewportVideoPlayingState(normalizedKey, false)
      options.onViewportError?.({ key: normalizedKey, error, silent })
      return false
    }
  }

  const ensureViewportIntersectionObserver = () => {
    if (!import.meta.client || viewportIntersectionObserver) {
      return viewportIntersectionObserver
    }

    viewportIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const key = viewportVideoKeys.get(entry.target)
          if (!key || viewportAutoplayedKeys.has(key) || viewportPendingKeys.has(key)) return

          viewportPendingKeys.add(key)
          void playViewportVideo(key, { silent: true, reset: true }).finally(() => {
            viewportPendingKeys.delete(key)
          })
        })
      },
      { threshold: viewportThreshold }
    )

    return viewportIntersectionObserver
  }

  /**
   * @description 绑定或解绑带唯一键的入屏视频元素。
   * @param {HTMLVideoElement|null} element - 视频元素。
   * @param {string|number} key - 视频唯一键。
   * @returns {void}
   */
  const setViewportVideoRef = (element, key) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return

    const previousElement = viewportVideoRefs.get(normalizedKey)
    if (previousElement && previousElement !== element) {
      viewportIntersectionObserver?.unobserve(previousElement)
      viewportVideoKeys.delete(previousElement)
    }

    if (!element) {
      viewportVideoRefs.delete(normalizedKey)
      viewportPendingKeys.delete(normalizedKey)
      return
    }

    viewportVideoRefs.set(normalizedKey, element)
    viewportVideoKeys.set(element, normalizedKey)

    if (previousElement !== element) {
      viewportAutoplayedKeys.delete(normalizedKey)
      element.load()
    }

    if (!viewportAutoplayedKeys.has(normalizedKey) && !viewportPendingKeys.has(normalizedKey)) {
      ensureViewportIntersectionObserver()?.observe(element)
    }
  }

  const resetViewportVideo = (key) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return
    viewportVideoRefs.get(normalizedKey)?.pause()
    viewportAutoplayedKeys.delete(normalizedKey)
    viewportPendingKeys.delete(normalizedKey)
    setViewportVideoPlayingState(normalizedKey, false)
  }

  const handleViewportVideoPlay = (key) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return
    viewportAutoplayedKeys.add(normalizedKey)
    setViewportVideoPlayingState(normalizedKey, true)
  }

  const handleViewportVideoPause = (key) => {
    setViewportVideoPlayingState(key, false)
  }

  const handleViewportVideoEnded = (key) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return
    viewportAutoplayedKeys.add(normalizedKey)
    setViewportVideoPlayingState(normalizedKey, false)
  }

  const handleViewportVideoError = (key) => {
    const normalizedKey = normalizeViewportVideoKey(key)
    if (!normalizedKey) return
    viewportAutoplayedKeys.delete(normalizedKey)
    viewportPendingKeys.delete(normalizedKey)
    setViewportVideoPlayingState(normalizedKey, false)
  }

  onUnmounted(() => {
    if (controlsRevealTimer) {
      clearTimeout(controlsRevealTimer)
      controlsRevealTimer = null
    }

    clearSeekTimers(heroSeekTimers)

    seekFixBindings.forEach((binding) => binding.cleanup())
    seekFixBindings.clear()

    viewportVideoRefs.forEach((video) => video.pause())
    viewportIntersectionObserver?.disconnect()
    viewportIntersectionObserver = null
    viewportVideoRefs.clear()
    viewportAutoplayedKeys.clear()
    viewportPendingKeys.clear()
  })

  return {
    videoRefs,
    isAutoPlay,
    currentCarouselIndex,
    activeVideoIndex,
    setVideoRef,
    handleCarouselChange,
    handleVideoPlay,
    handleVideoPause,
    handleVideoSeeking,
    handleVideoSeeked,
    handleVideoEnded,
    bindVideoSeekFix,
    unbindVideoSeekFix,
    syncVideoBindings,
    viewportVideoPlayingStates,
    getViewportVideo,
    setViewportVideoRef,
    playViewportVideo,
    resetViewportVideo,
    handleViewportVideoPlay,
    handleViewportVideoPause,
    handleViewportVideoEnded,
    handleViewportVideoError,
  }
}
